import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  RATING_AXES,
  useOrderRating,
  useSaveOrderRating,
} from "@/features/rfq/api/ratings";
import { Stars, StarPicker } from "@/features/rfq/components/Stars";

/**
 * Ocena współpracy przy konkretnym zamówieniu — sekcja na karcie zamówienia.
 *
 * Naturalne miejsce: ocenia się zaraz po odbiorze, patrząc na to zlecenie, a nie
 * wchodząc na kartę firmy. Ta sama ocena widnieje potem w podsumowaniu podwykonawcy.
 *
 * Sekcja w ogóle się nie pokazuje, dopóki zamówienie nie jest odebrane — żeby nie
 * zachęcać do oceniania roboty, która jeszcze trwa. Gdy już się pokaże, siedzi wysoko
 * i w czerwonej obwolucie: to jedyny moment, w którym ktokolwiek o ocenie pamięta.
 */

export function OrderRatingSection({ orderId }: { orderId: string }) {
  const { data } = useOrderRating(orderId);
  const save = useSaveOrderRating(orderId);

  const [editing, setEditing] = useState(false);
  const [scores, setScores] = useState({ quality: 4, cooperation: 4, timeliness: 4 });
  const [comment, setComment] = useState("");

  // Poprawianie oceny startuje od tego, co już jest; nowa — od czterech gwiazdek.
  useEffect(() => {
    if (!data?.rating) return;
    setScores({
      quality: data.rating.quality,
      cooperation: data.rating.cooperation,
      timeliness: data.rating.timeliness,
    });
    setComment(data.rating.comment);
  }, [data?.rating]);

  if (!data || !data.is_rateable) return null;

  const { rating, can_rate: canRate } = data;

  const submit = () =>
    save.mutate({ ...scores, comment }, { onSuccess: () => setEditing(false) });

  return (
    <section className="mb-6">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <Star size={16} className="text-[var(--color-text-muted)]" />
        Ocena współpracy
      </h2>

      <div className="border-2 border-[var(--color-primary)] bg-white p-5 dark:bg-gray-800">
        {editing ? (
          <div className="space-y-3">
            {RATING_AXES.map((axis) => (
              <div key={axis.key} className="flex items-center justify-between gap-4">
                <span className="text-sm text-[var(--color-text-primary)]">{axis.label}</span>
                <StarPicker
                  value={scores[axis.key]}
                  onChange={(v) => setScores((prev) => ({ ...prev, [axis.key]: v }))}
                />
              </div>
            ))}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Komentarz (opcjonalnie)"
              className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
            />
            <p className="text-xs text-[var(--color-text-muted)]">
              Ocena jest widoczna wyłącznie dla GK ELEKTIM — podwykonawca jej nie zobaczy.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(false)}>
                Anuluj
              </Button>
              <Button onClick={submit} disabled={save.isPending}>
                {save.isPending ? "Zapisywanie…" : "Zapisz ocenę"}
              </Button>
            </div>
          </div>
        ) : rating ? (
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                Średnia
              </p>
              <div className="flex items-center gap-2">
                <Stars value={rating.average} size={16} />
                <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                  {rating.average.toFixed(2)}
                </span>
              </div>
            </div>
            {RATING_AXES.map((axis) => (
              <div key={axis.key}>
                <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  {axis.label}
                </p>
                <Stars value={rating[axis.key]} />
              </div>
            ))}
            <div className="w-full text-xs text-[var(--color-text-muted)]">
              {rating.rated_by_name && `${rating.rated_by_name} · `}
              {new Date(rating.created_at).toLocaleDateString("pl")}
              {rating.comment && (
                <span className="block text-[var(--color-text-secondary)]">{rating.comment}</span>
              )}
            </div>
            {canRate && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Zmień ocenę
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              {canRate
                ? "Zamówienie jest odebrane — można ocenić współpracę."
                : "Zamówienie nie zostało jeszcze ocenione. Ocenia Zamawiający albo dyrektor techniczny."}
            </p>
            {canRate && (
              <Button onClick={() => setEditing(true)}>
                <Star size={14} className="mr-1.5 inline" /> Oceń współpracę
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
